import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa'
import { MdFilePresent } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'

import { AccountCircle, ArrowBack, ArrowForward, CalendarMonth, CheckCircleOutline, ExpandMore, NoteAlt, Quiz, Send, TableView } from '@mui/icons-material'
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, CircularProgress, Grid, Typography } from '@mui/material'
import { format } from 'date-fns'

import { useGET_studentAssignmentByIdQuery } from 'redux/api/assignmentsApi'
import {
  useDELETE_submissionMutation,
  useDELETE_submission_attachmentsMutation,
  usePOST_submissionMutation,
  usePOST_submission_attachmentsMutation,
  usePUT_submissionMutation,
  usePUT_submissionPublishMutation,
} from 'redux/api/submissionsApi'

import { Form, Formik } from 'formik'

import BoxTitle from 'components/atoms/BoxTitle'
import AttachmentButton from 'components/atoms/buttons/AttachmentButton'
import SubmitButton from 'components/atoms/buttons/SubmitButton'
import RichTextField from 'components/atoms/fields/RichTextField'
import DialogPopup from 'components/molecules/DialogPopup'
import Rubric from 'components/molecules/Rubric'


import { useMedia } from 'utils/customHooks/useMedia'
import useCrudData from 'utils/customHooks/useCrudData'
import {
  ELEMENT_RADIUS_MID,
  PRIMARY_TRANSPARENT_BLUE,
  PRIMARY_TRANSPARENT_GREEN,
  PRIMARY_TRANSPARENT_PINK,
  PRIMARY_YELLOW,
  PURE_BLACK,
} from 'utils/customizedTheme'
import formModel, { initialValues, validationSchema } from 'utils/formModels/studentAssignmentModel'
import { crudColorMap, crudIconMap, formatDateTime, isImage } from 'utils/helpers'

import beeBooks from 'assets/images/student/bee-books.jpg'

import { ActionType, DocumentStatusType } from 'types'

import ConversationForm from './ConversationForm'
import PeerAssessments from 'components/utilized/student/PeerAssessments'
import { useTranslateGrade } from 'utils/customHooks/useTranslateGrade'
import { useSelector } from 'react-redux'
import { RootState } from 'redux/store'


interface IProps {
  action: ActionType
  id: string
}

const slideProps = {
  mountOnEnter: true,
  unmountOnExit: true,
  timeout: { enter: 225, exit: 195 }
};


const StudentAssignmentForm: FC<IProps> = ({ action, id }) => {

  const { t } = useTranslation()
  const navigate = useNavigate()

  const user = useSelector((state: RootState) => state['user'].user)

  const backToSlug = user?.role === 'parent' ? '/parent-assignments' : '/student-assignments'
  const navSlug = '/student-assignments'

  const popupActionTitles = {
    'rubric' : t('common.rubric'),
    'model-answers' : t('common.model-answer'),
    'general-notes' : t('common.notes'),
    'peer-assessments' : t('common.peer-assessments'),
  }

  const [formSubmitCount, setFormSubmitCount] = useState(0)
  const [popupAction, setPopupAction] = useState('')
  const [publish, setPublish] = useState(false)
  const [expandedImage, setExpandedImage] = useState(null)
  const [expandedImageTitle, setExpandedImageTitle] = useState(null)

  const [translateGrade, gradeColor] = useTranslateGrade();

  const onSuccess = () => {    
    setFormSubmitCount(9)
    setTimeout(() => window.location.reload(), 3000);
  }

  const { isUnder900px, isUnder1536px } = useMedia()

  const { addResponseData, editResponseData, isLoading, entity, handleSubmit } = useCrudData({
    backToSlug,
    onSuccess,
    entityName: `${t('common.assignment')}`,
    entityId: id,
    getEntityQuery: useGET_studentAssignmentByIdQuery,
    createEntityMutation: usePOST_submissionMutation,
    editEntityMutation: usePUT_submissionMutation,
    deleteEntityMutation: useDELETE_submissionMutation,
  })
  const { submission_description, attachments, submission_materials, due_datetime, effective_datetime } = formModel()
  const [PUBLISH_submission, { isLoading: submissionEditLoading, data: patchResponseData }] =
    usePUT_submissionPublishMutation()
  const [POST_submission_attachments, { isLoading: submissionSaveLoading }] =
    usePOST_submission_attachmentsMutation()
  const [DELETE_submission_attachments, { isLoading: submissionDeleteLoading }] =
    useDELETE_submission_attachmentsMutation()

  const statusType = entity?.submission?.status as DocumentStatusType
  const isPublished = statusType === 'published' || !!patchResponseData?.id

  const viewOnly = user?.role !== 'student' || entity?.assignment_status !== 'published' || isPublished || entity?.gradebook_assessment

  const serverValues = {
    ...entity,
    submission_materials: entity?.submission?.materials,
    submission_description: entity?.submission?.description,
  }

  const onAssignmentsNavigationClick = (assignmentId) => {
    if(assignmentId)
      navigate(`${navSlug}/edit/${assignmentId}`)
  }

  const getStatus = () => {
      if(publish || serverValues?.status === 'published')return 'published'
      return 'draft'
  }


  return (
    <>
    <Formik
      enableReinitialize
      initialValues={serverValues || initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const data = {
          description: values?.submission_description,
          status: getStatus(),
          assignment_id: Number(id),
        }
        if (entity?.submission?.id) {
          handleSubmit('edit', entity?.submission.id, data)
        } else {
          handleSubmit('add', id, data)
        }
      }}
    >
      {({ values, setFieldValue, errors }) => {
        return (
          <>
            <Form>
              <Grid container spacing={4} sx={{ marginTop: '-1rem' }}>
                <Grid item xs={6} xl={3} sx={{ display: 'flex', justifyContent: 'center', order: 1 }}>
                  <Button
                    color='black'
                    onClick={() => onAssignmentsNavigationClick(entity?.previous_assignment_id)}
                    sx={{
                      minHeight: 50,
                      width: '100%',
                      backgroundColor: PRIMARY_YELLOW,
                      borderRadius: ELEMENT_RADIUS_MID,
                      fontWeight: '400 !important'
                    }}
                  >
                    <ArrowBack style={{ fontSize: '2rem', marginRight: '10px' }} />
                    {`${t('assignments.previous-assignment')}`}
                  </Button>
                </Grid>
                <Grid item xs={6} xl={3} sx={{ display: 'flex', justifyContent: 'center', order: isUnder1536px ? 3 : 2 }}>
                  <Button
                    color='black'
                    sx={{
                      minHeight: 50,
                      width: '100%',
                      lineHeight: '1.15',
                      backgroundColor: PRIMARY_TRANSPARENT_GREEN,
                      borderRadius: ELEMENT_RADIUS_MID,
                      fontWeight: '400 !important'
                    }}
                  > 
                    {t('common.assigned-on')}:<br/>
                    { serverValues.effective_datetime && formatDateTime(serverValues.effective_datetime) }
                    <CalendarMonth style={{ fontSize: '2rem', marginLeft: '10px' }} />
                  </Button>
                </Grid> 
                <Grid item xs={6} xl={3} sx={{ display: 'flex', justifyContent: 'center', order: isUnder1536px ? 4 : 3 }}>
                  <Button
                    color='black'
                    sx={{
                      minHeight: 50,
                      width: '100%',
                      lineHeight: '1.15',
                      backgroundColor: PRIMARY_TRANSPARENT_BLUE,
                      borderRadius: ELEMENT_RADIUS_MID,
                      fontWeight: '400 !important'
                    }}
                  > 
                    {t('common.deadline')}:<br/>
                    {serverValues.due_datetime && formatDateTime(serverValues.due_datetime)}
                    <CalendarMonth style={{ fontSize: '2rem', marginLeft: '0px' }} />
                  </Button>
                </Grid>
                <Grid item xs={6} xl={3} sx={{ display: 'flex', justifyContent: 'center', order: isUnder1536px ? 2 : 4 }}>
                  <Button
                    color='black'
                    onClick={() => onAssignmentsNavigationClick(entity?.next_assignment_id)}
                    sx={{
                      minHeight: 50,
                      width: '100%',
                      backgroundColor: PRIMARY_TRANSPARENT_PINK,
                      borderRadius: ELEMENT_RADIUS_MID,
                      fontWeight: '400 !important'
                    }}
                  >
                    {`${t('assignments.next-assignment')}`}
                    <ArrowForward style={{ fontSize: '2rem', marginLeft: '10px' }} />
                  </Button>
                </Grid>
              </Grid>

              <Grid container spacing={4} sx={{ marginTop: '1rem' }}>
                {/* What you need to do */}
                <Grid item xs={12} xl={ !entity || entity?.materials.length === 0 ? 12 : 6}>
                  <Box className="thick-border"
                    sx={{
                      height: '100%',
                    }}
                  >
                    <BoxTitle title={`${t('assignments.what-you-need-to-do')}`} />
                    <Grid container>
                      <Grid
                        item
                        xs={12}
                        sx={{
                          backgroundColor: PRIMARY_TRANSPARENT_BLUE,
                          borderRadius: ELEMENT_RADIUS_MID,
                          display: 'flex',
                          justifyContent: 'space-evenly',
                          alignItems: 'center',
                          marginTop: '1rem',
                        }}
                      >
                        <Typography sx={{ width: '100%', padding: '10px', fontSize: '1.2rem', fontWeight: '400' }}>
                          {entity?.title}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sx={{ padding: '10px' }}>
                        <Typography
                          dangerouslySetInnerHTML={{
                            __html: entity?.description,
                          }}
                        />
                        {entity?.grading_components?.map(g => <div key={g.id} style={{ marginTop: '10px' }}><b>&bull; {g.title}</b> <Typography
                          dangerouslySetInnerHTML={{
                            __html: g.description,
                          }}
                        /></div>)
                        }

                        {entity?.rubric?.id && 
                        <Button
                          variant='outlined'
                          color='black'
                          onClick={() => setPopupAction('rubric')}
                          sx={{ marginTop: '20px' }}
                        >
                          <TableView />
                          {`${t('common.rubric')}`}
                        </Button>
                        }
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                {/* Help materials */}

                { entity?.materials.length > 0  &&
                <Grid item xs={12} xl={6}>
                  <Box  className="thick-border" sx={{
                      height: '100%',
                    }}>
                    <BoxTitle title={`${t('assignments.help-materials')}`} />
                    <Grid container>
                      <Grid
                        item
                        xs={12}
                        sx={{
                          backgroundColor: PRIMARY_TRANSPARENT_BLUE,
                          borderRadius: ELEMENT_RADIUS_MID,
                          display: 'flex',
                          justifyContent: 'space-evenly',
                          alignItems: 'center',
                          marginTop: '1rem',
                        }}
                      >
                        <Typography sx={{ width: '100%', padding: '10px', fontSize: '1.4rem', fontWeight: 'bold' }}>
                          {`${t('common.course')}: ${entity?.course}`}
                        </Typography>
                      </Grid>

                      {entity?.materials.filter(material => isImage(material.url)).map((material, i) => {
                          return (
                            <Grid item key={i} xs={3} sx={{ marginTop: '1rem', padding: '10px' }}>
                              <div className='box-shadow'
                              onClick={() => {
                                setExpandedImageTitle(material.title);
                                setExpandedImage(material.url);
                              }}
                               style={{
                                cursor: 'zoom-in',
                                aspectRatio: 1.5,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center center',
                                backgroundImage: `url('${material.url}')`
                              }}></div>
                            </Grid>
                          )
                        })}


                      <DialogPopup
                        title={expandedImageTitle}
                        open={!!expandedImage}
                        onClose={() => {
                          setExpandedImage(null);
                          setExpandedImageTitle(null)
                        }}
                        popupWidth={'1000px'}
                      >
                        <img src={expandedImage} style={{ maxWidth: '100%' }} />
                      </DialogPopup>


                      {entity?.materials.filter(material => !isImage(material.url)).map((material, i) => {
                          return (
                            <Grid item key={i} xs={12} sx={{ marginTop: '1rem' }}>
                              <a
                                href={material.url}
                                target='_blank'
                                rel='noreferrer'
                                style={{ color: PURE_BLACK, textDecoration: 'underline', cursor: 'pointer' }}
                              >
                                <MdFilePresent
                                  style={{ margin: '0 0.5rem -0.25rem 0', fontSize: '1.25rem' }}
                                />
                                {material.title}
                              </a>
                            </Grid>
                          )
                        })}


                    </Grid>
                  </Box>
                </Grid>
                 }

                {/* Submissions */}
                {entity?.gradebook_assessment ? (
                <Grid item xs={12} xl={12}>
  
                      {/* Grade */}
                        <Box className="thick-border">

                          <Grid container sx={{ alignItems: 'end' }}>
                            <Grid item xs={12} xl={9} sx={{ marginTop: '1rem', alignItems: 'center' }}>
                              <Typography sx={{fontSize: '1.6rem', lineHeight: '1.2', display: 'flex'}}>
                                {t('common.reviewed-by')}:
                                <AccountCircle sx={{ marginLeft: '1rem', fontSize: '2rem', color: 'gray' }} />{' '}
                                {entity?.gradebook_assessment?.teacher_name}{' '}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} xl={3} sx={{ marginTop: '1rem', textAlign: 'right' }}>
                              <Typography
                                variant='h3'
                                align='right'
                                sx={{
                                  fontFamily: 'NewsReader',
                                  fontWeight: '600',
                                  fontSize: '1.75rem',
                                  padding: '1rem',
                                  display: isUnder1536px ? 'block' : 'inline-block',
                                  color: '#fff',
                                  backgroundColor: gradeColor(entity?.gradebook_assessment?.final_grade_percentage),
                                  borderRadius: ELEMENT_RADIUS_MID,
                                }}
                              >
                                {`${t('common.grade')}: ${translateGrade(entity?.gradebook_assessment?.final_grade_percentage)}`}
                              </Typography>
                            </Grid>  
                          </Grid>

                          
                          <Grid
                            item
                            xs={12}
                            sx={{
                              marginTop: '1rem',
                              padding: '1rem',
                              display: 'flex',
                              flexDirection: 'column',
                              backgroundColor: PRIMARY_TRANSPARENT_BLUE,
                              borderRadius: ELEMENT_RADIUS_MID,
                            }}
                          >
                            <Typography>
                              {format(new Date(entity?.gradebook_assessment?.created_at), 'Pp')}{' '}
                            </Typography>
                            <Typography
                              variant='h6'
                              dangerouslySetInnerHTML={{
                                __html: entity?.gradebook_assessment?.comment,
                              }}
                            />
                          </Grid>
                        </Box>
                      
                      {/* Sent submission */}
                        <Box className="thick-border"
                          sx={{
                            marginTop: '2rem',
                          }}
                        >
                          <BoxTitle
                            title={`${t('assignments.reviewed')}`}
                            icon={<CheckCircleOutline color='success' sx={{ marginLeft: '1rem', fontSize: '3rem' }} />}
                          />
                          <Grid
                            item
                            xs={12}
                            sx={{
                              marginTop: '1rem',
                              backgroundColor: PRIMARY_TRANSPARENT_GREEN,
                              padding: '1rem',
                              borderRadius: ELEMENT_RADIUS_MID,
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <Typography
                              variant='h6'
                              dangerouslySetInnerHTML={{
                                __html: entity?.submission?.description,
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sx={{ marginTop: '1rem' }}>
                            <AttachmentButton
                              fieldProps={attachments}
                              values={values}
                              errors={errors}
                              setFieldValue={setFieldValue}
                              disabled={viewOnly}
                              id={addResponseData?.id || editResponseData?.id || entity?.submission?.id}
                              formSubmitCount={formSubmitCount}
                              savedAttachmentsKey={'submission_materials'}
                              saveAttachmentsApiCall={POST_submission_attachments}
                              deleteAttachmentsApiCall={DELETE_submission_attachments}
                            />
                          </Grid>
                        </Box>
                </Grid>
                ) : (
                  <Grid item xs={12}>
                    <Box className="thick-border">
                      {(!entity?.submission?.status ||
                        (entity?.submission?.status && entity?.submission?.status !== 'published')) && (
                        <Grid container sx={{ alignItems: 'center'}}>
                          <Grid item xs={8}>
                            <BoxTitle title={`${t('assignments.your-turn')}`} />
                          </Grid>
                          <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'right' }}>
                            <img alt='EsyBee' style={{ maxHeight: '200px', position: 'relative', zIndex: '20', bottom: '-40px' }} src={beeBooks} />
                          </Grid>
                        </Grid>
                      )}
                      {!entity?.gradebook_assessment &&
                        entity?.submission?.status &&
                        entity?.submission?.status === 'published' && (
                          <BoxTitle title={`${t('assignments.reviewed-soon')}`} />
                        )}
                      <Grid item xs={12} sx={{ marginTop: '1rem' }}>
                        <RichTextField
                          fieldProps={{ ...submission_description }}
                          label={submission_description?.label}
                          disabled={viewOnly}
                        />
                      </Grid>
                      <Grid item xs={12} sx={{ marginTop: '1rem' }}>
                        <AttachmentButton
                          fieldProps={attachments}
                          values={values}
                          errors={errors}
                          setFieldValue={setFieldValue}
                          disabled={viewOnly}
                          formSubmitCount={formSubmitCount}
                          id={addResponseData?.id || editResponseData?.id || entity?.submission?.id}
                          savedAttachmentsKey={'submission_materials'}
                          saveAttachmentsApiCall={POST_submission_attachments}
                          deleteAttachmentsApiCall={DELETE_submission_attachments}
                        />
                      </Grid>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Grid container>
                    <Grid item xs={12} lg={6}>
                      <Button
                        variant='outlined'
                        color='black'
                        onClick={() => navigate(backToSlug)}
                        sx={{ 
                          marginBottom: '10px',
                          minHeight: '2.5rem',
                          width: isUnder900px ? '100%' : 'auto'
                        }}
                      >
                        <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                        {`${t('common.back')}`}
                      </Button>

                      
                      { entity?.model_answers.length > 0 &&
                      <Button
                        variant='outlined'
                        color='black'
                        onClick={() => setPopupAction('model-answers')}
                        sx={{ 
                          minHeight: '2.5rem',
                          marginBottom: '10px',
                          marginLeft: isUnder900px ? 0 : '5px',
                          width: isUnder900px ? '100%' : 'auto'
                        }}
                      >
                        <Quiz sx={{ marginRight: '5px' }} />
                        {`${t('common.model-answer')}`}
                      </Button>
                      }

                      { entity?.all_purpose_items.length > 0 &&  
                      <Button
                        variant='outlined'
                        color='black'
                        onClick={() => setPopupAction('general-notes')}
                        sx={{ 
                          minHeight: '2.5rem',
                          marginBottom: '10px',
                          marginLeft: isUnder900px ? 0 : '5px',
                          width: isUnder900px ? '100%' : 'auto'
                        }}
                      >
                        <NoteAlt sx={{ marginRight: '5px' }} />
                        {`${t('common.notes')}`}
                      </Button>
                     }




                    </Grid>
                    <Grid item xs={12} lg={6} sx={{ textAlign: 'right' }}>

                         {user?.role === 'student' && entity?.has_peer_assessment &&
                          <Button
                              variant='contained'
                              color={'blue'}
                              sx={{ 
                                minHeight: '2.5rem',
                                marginBottom: '10px',
                                width: isUnder900px ? '100%' : 'auto',
                              }}
                              fullWidth
                              onClick={() => setPopupAction('peer-assessments')}
                              disabled={isLoading}
                              className='submit-btn'
                              disableElevation
                            >
                              {`${t('common.peer-assessments')}`}
                          </Button>
                        }

                        {!viewOnly && (<>
                          <SubmitButton
                            color={'primary'}
                            text={`${t('common.save-changes')}`}
                            icon={crudIconMap[action]}
                            sx={{ 
                              display: 'inline-flex',
                              minHeight: '2.5rem',
                              marginBottom: '10px',
                              width: isUnder900px ? '100%' : 'auto',
                              marginLeft: isUnder900px ? 0 : '5px' 
                            }}
                            isLoading={isLoading}
                          />

                          <Button
                              type='submit'
                              variant='contained'
                              color={'success'}
                              sx={{ 
                                minHeight: '2.5rem',
                                marginBottom: '10px',
                                width: isUnder900px ? '100%' : 'auto',
                                marginLeft: isUnder900px ? 0 : '10px' 
                              }}
                              fullWidth
                              onClick={() => setPublish(true)}
                              disabled={isLoading}
                              className='submit-btn'
                              disableElevation
                            >
                              {isLoading ? (
                                <CircularProgress variant='indeterminate' size={20} sx={{ color: '#000' }} />
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} className="transparent-background">
                                  {`${t('common.submit')}`}
                                  <Send sx={{ fontSize: '1.5rem', marginLeft: '10px' }} />
                                </Box>
                              )}
                          </Button>
                        </>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Form>

            {entity && entity.conversation_id && user?.role === 'student' &&
            <ConversationForm
              tabText={`${t('common.conversation')}`}
              action={action}
              conversationId={entity.conversation_id}
              viewOnly={entity.assignment_status !== 'published'}
            />
            }
          </>
        )
      }}
    </Formik>

    <DialogPopup
    title={popupActionTitles[popupAction]}
    open={popupAction!==''}
    onClose={() => {
      setPopupAction('')
    }}
    popupWidth={'1040px'}
    >
      {popupAction === 'rubric' && entity?.rubric?.id && <Rubric
       id={entity?.rubric?.id} 
       extraCredit={entity?.has_extra_credit ? entity?.extra_credit : 0}
       rubricGrades={entity?.gradebook_assessment?.rubric_grades ?? []} />}

      {popupAction === 'peer-assessments' && entity?.has_peer_assessment && <PeerAssessments assignment={entity} assignment_id={id} />}


      {popupAction === 'model-answers' && <>

          {entity?.model_answers.map((answer, index) => 
                  <Accordion 
                    TransitionProps={slideProps}
                    key={answer.id} 
                    defaultExpanded={index === 0}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        {t('common.model-answer')} {index+1}
                    </AccordionSummary>
                    <AccordionDetails>  
                        <>
                          <span dangerouslySetInnerHTML={{ __html: answer.text }} />
                        </>   
                    </AccordionDetails>
                  </Accordion>  
                ) 
          }   

      </>}

      {popupAction === 'general-notes' && <>

          {entity?.all_purpose_items.map((note, index) => 
                          <Accordion 
                            TransitionProps={slideProps}
                            key={note.id} 
                            defaultExpanded={index === 0}>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                 {t('common.all-purpose-item')}: {note.title}
                            </AccordionSummary>
                            <AccordionDetails>  
                                <>
                                  <span dangerouslySetInnerHTML={{ __html: note.text }} />
                                </>   
                            </AccordionDetails>
                          </Accordion>  
                        ) 
        }   


      </>}
    </DialogPopup>
    </>
  )
}
export default StudentAssignmentForm
